import React, { useState } from "react";
import {
  Card,
  CardHeader,
  Breadcrumbs as MuiBreadcrumbs,
  Typography,
  Paper as MuiPaper,
  CardContent,
  Box,
  Chip,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { purple, blue, green, red, orange, yellow, grey } from "@mui/material/colors";

import EditDelete from "../../../../components/EditDelete";
import Toolbar from "../../../../components/Toolbar";

import { useDialog } from "../../../../contexts/DialogContext";
import { getTimeStamp } from "../../../../utils/common";
import useWindowDimensions from "../../../../utils/windowDimensions";
import AdvanceFilter from "./AdvanceFilter";
import useUser from "../../../../hooks/useUser";
import useMainContentDimensions from "../../../../utils/mainContentDimensions";
import DisconnectUserSession from "../../../hms/hotel_users/components/DisconnectUserSession";
import { useDispatch } from "react-redux";
import { showErrorSnackbar } from "../../../../redux/slices/snackbar";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

const breadCrump = () => {
  return (
    <Breadcrumbs aria-label="Breadcrumb" mt={2}>
      <Typography>Masters</Typography>
      <Typography>User List</Typography>
      <Typography>List</Typography>
    </Breadcrumbs>
  );
};

const userStatus = (status) => {
  const chipStyles = {
    width: "100px", // Set the desired width for all chips
    textAlign: "center", // Center align the text in each chip
  };

  if (status === "active") {
    return <Chip label="Active" color="primary" sx={chipStyles} />;
  } else if (status === "online") {
    return <Chip label="Online" color="success" sx={chipStyles} />;
  } else if (status === "expired") {
    return <Chip label="Expired" color="warning" sx={chipStyles} />;
  } else if (status === "offline") {
    return (
      <Chip label="Offline" sx={{ ...chipStyles, backgroundColor: "grey.500", color: "white" }} />
    );
  } else if (status === "suspended") {
    return (
      <Chip label="Suspended" sx={{ ...chipStyles, backgroundColor: red[700], color: "white" }} />
    );
  } else if (status === "on_hold") {
    return (
      <Chip label="On Hold" sx={{ ...chipStyles, backgroundColor: yellow[700], color: "white" }} />
    );
  }
};

const UserListCustomDataGrid = (props) => {
  const { isLoadingDownload, downloadFileType } = useUser();

  const { height, width } = useWindowDimensions();
  const { width: mainContentWidth } = useMainContentDimensions();
  const [selectedRows, setSelectedRows] = useState([]);
  const dispatch = useDispatch();
  const {
    editPage,
    addPage,
    trans,
    rows,
    rowCount,
    payload,
    setPayload,
    isLoading,
    id,
    handleSortModelChange,
    sortModel,
    downloadFile,
    openDownloadDialog,
    refreshPage,
    setFilterResetFunc,
    paginationModel,
    paginationMeta,
    paging,
    onPaginationModelChange,
  } = props;
  console.log(rowCount, "loading child component");

  const { openDialog } = useDialog();

  const columns = [
    {
      field: "userName",
      headerName: "User Name",
      renderCell: (params) => <Link to={"/user/details?id=" + params.id}>{params.value}</Link>,
      width: 200,
    },
    {
      field: "fullName",
      headerName: "Full Name",
      width: 200,
    },
    {
      field: "userStatus",
      headerName: "Status",
      renderCell: (params) => {
        return userStatus(params.row["userStatus"]);
      },
      width: 200,
    },
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: 200,
    },
    {
      field: "rateFullName",
      headerName: "Sub Plan",
      width: 200,
    },
  ];

  const openFilterDialog = () => {
    return openDialog(
      "advance_filter",
      "Advance Filter",
      <AdvanceFilter
        payload={payload}
        setPayload={setPayload}
        openFilterDialog={openFilterDialog}
        setFilterResetFunc={setFilterResetFunc}
      />,
    );
  };
  const disconnectUserSession = () => {
    if (selectedRows.length != 0) {
      return openDialog(
        "disconnect_user_session",
        "Disconnect User Session",
        <DisconnectUserSession selectedRows={selectedRows} refreshPage={refreshPage} />,
      );
    } else {
      dispatch(showErrorSnackbar("No online users or users not selected"));
    }
  };

  return (
    <Card sx={{ width: "100%", maxWidth: mainContentWidth - 64 }}>
      <CardHeader
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // Stack on small screens
          justifyContent: "space-between",
          alignItems: "flex-start", // Adjust alignment as needed
        }}
        action={
          <Toolbar
            addPage={addPage}
            downloadFile={downloadFile}
            openFilterDialog={null}
            openDownloadDialog={openDownloadDialog}
            refreshPage={refreshPage}
            isLoadingDownload={isLoadingDownload}
            downloadFileType={downloadFileType}
            disconnectUserSession={disconnectUserSession}
          />
        }
      />
      <CardContent sx={{ height: height - 180, display: "flex", flexDirection: "column" }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <DataGrid
            checkboxSelection
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            rowCount={rowCount}
            getRowId={(row) => row[id]}
            columns={columns}
            rows={rows}
            page={paging.pageNumber}
            pageSize={paging.pageSize}
            paginationMeta={paginationMeta}
            loading={isLoading}
            pageSizeOptions={[25, 50, 100]}
            paginationMode="server"
            onPaginationModelChange={onPaginationModelChange}
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            paginationModel={paginationModel}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
            }
            sx={{
              "& .MuiDataGrid-row.even-row": {
                backgroundColor: "#f9f9f9", // Light gray for even rows
              },
              "& .MuiDataGrid-row.odd-row": {
                backgroundColor: "#ffffff", // White for odd rows
              },
            }}
            onRowSelectionModelChange={(newRowSelectionModel, data) => {
              const filteredRows = rows.filter((user) =>
                newRowSelectionModel.includes(user.radUserId),
              );
              const filteredRadAcctIds = filteredRows
                .filter((user) => user.radAcctId != null)
                .map((user) => user.radAcctId);
              setSelectedRows(filteredRadAcctIds);
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
UserListCustomDataGrid.propTypes = {
  editPage: PropTypes.func.isRequired,
  addPage: PropTypes.func.isRequired,
  trans: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  rowCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  handleSortModelChange: PropTypes.func.isRequired,
  sortModel: PropTypes.object.isRequired,
};
export default UserListCustomDataGrid;
